/* The render orchestrator. `renderAll(state)` calls every section renderer
 * in the EXACT order old load() did (service/review/index.html ~3436-3437):
 *   renderExec(); renderSearch(); renderEntity(); renderAI();
 *   renderProfiles(); renderWiki(); renderConflicts(); renderActions();
 *   renderAppendix();
 * F2a ported renderExec; F2b ported renderSearch/renderEntity/renderAI;
 * F2c ported renderProfiles/renderWiki/renderConflicts/renderAppendix;
 * F2d (this task) lands the real `actions` renderer (views/actions.ts,
 * §7 Action plan / AM workspace board) — every renderer now does real
 * work; `renderAll`'s call order is otherwise unchanged.
 */
import { renderExec } from './exec.ts';
import { renderSearch } from './search.ts';
import { renderEntity } from './entity.ts';
import { renderAI } from './ai.ts';
import { renderProfiles } from './profiles.ts';
import { renderWiki } from './wiki.ts';
import { renderConflicts } from './conflicts.ts';
import { renderActions } from './actions.ts';
import { renderAppendix } from './appendix.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export type SectionRenderer = (state: AppPayloadState) => void;

// `satisfies` (not a Record annotation): keeps every key's renderer typed as
// present, so renderAll's dot-calls stay non-optional under
// noUncheckedIndexedAccess while the map still checks against SectionRenderer.
export const renderers = {
  exec: renderExec,
  search: renderSearch,
  entity: renderEntity,
  ai: renderAI,
  profiles: renderProfiles,
  wiki: renderWiki,
  conflicts: renderConflicts,
  actions: renderActions,
  appendix: renderAppendix,
} satisfies Record<string, SectionRenderer>;

export function renderAll(state: AppPayloadState): void {
  renderers.exec(state);
  renderers.search(state);
  renderers.entity(state);
  renderers.ai(state);
  renderers.profiles(state);
  renderers.wiki(state);
  renderers.conflicts(state);
  renderers.actions(state);
  renderers.appendix(state);
}
